import {
  ChangeDetectionStrategy,
  Component,
  signal,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
  inject,
  ChangeDetectorRef
} from '@angular/core';
import { JsonPipe, NgIf } from '@angular/common';
import {
  AsyncPipe,
  CommonModule,
  NgForOf,
} from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  ValidatorFn,
} from '@angular/forms';
import {
  TuiRepeatTimes,
  TuiContext,
  TuiIdentityMatcher,
  TuiStringHandler,
  TuiValidationError
} from '@taiga-ui/cdk';
import { TuiDay, TuiTime, TuiDayRange } from '@taiga-ui/cdk/date-time';
import type { AbstractControl } from '@angular/forms';
import {
  TuiButton,
  TuiLink,
  TuiPopup,
  TuiScrollbar,
  TuiTitle,
  TuiError,
  TuiGroup,
  TuiIcon,
  TuiLabel,
  TuiTextfield,
  TuiDataList,
  TuiCalendar,
  TuiNotification
} from '@taiga-ui/core';
import {
  TuiBadge,
  TuiDrawer,
  TuiTabs,
  TuiBlock,
  TuiCheckbox,
  TuiChevron,
  TuiDataListWrapper,
  TuiFieldErrorPipe,
  TuiInputNumber,
  TuiPassword,
  TuiRadio,
  TuiSelect,
  TuiTooltip,
  tuiInputPhoneInternationalOptionsProvider,
  TuiSortCountriesPipe,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiAvatar,
  TuiBadgedContent,
  TuiFiles,
  TuiButtonClose,
  tuiFilesAccepted,
  TuiInputDate,
} from '@taiga-ui/kit';
import { TuiInputChip } from '@taiga-ui/kit';
import {
  TuiInputDateModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiInputSliderModule,
  TuiInputTimeModule,
  TuiTextfieldControllerModule,
  TuiMultiSelectModule,
  TuiInputDateRangeModule
} from '@taiga-ui/legacy';
import {
  TuiHeader,
  TuiNavigation,
  TuiForm,
  TuiCell,
} from '@taiga-ui/layout';
import type { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { getCountries } from 'libphonenumber-js';
import { defer, Subscription, finalize, map, of, Subject, switchMap, timer, Observable } from 'rxjs';
import { TuiTextareaModule } from '@taiga-ui/legacy';
import { InputHeading } from '../../input-heading/input-heading';
import { CurrencyService } from '../../../services/currency.service';
import { ApiService } from '../../../services/api.service';
import { NotificationService } from '../../../services/notification.service';
import { TextDirectionDirective } from '../../../directives/text-direction.directive';
import { PermissionDisableDirective } from '../../../directives/permission-disable.directive';
import { Region } from '../../../interfaces/region.interface';
import { TuiCurrencyPipe } from '@taiga-ui/addon-commerce';
import { extractApiError } from '../../../utils/extract-api-error.util';
import { tuiDateFormatProvider } from '@taiga-ui/core';

@Component({
  selector: 'app-facility-drawer',
  standalone: true,
  templateUrl: './facility-drawer.html',
  styleUrl: './facility-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    TuiMultiSelectModule,
    TuiTextfieldControllerModule,

    // Taiga UI Core & Kit Standalone Components
    TuiButton,
    TuiLink,
    TuiPopup,
    TuiTitle,
    TuiError,
    TuiLabel,
    TuiTextfield,
    TuiTabs,
    TuiDrawer,
    TuiHeader,
    TuiNavigation,
    TuiChevron,
    TuiDataListWrapper,
    TuiFieldErrorPipe,
    TuiInputNumber,
    TuiRadio,
    TuiSelect,
    TuiForm,
    TuiSwitch,
    TuiCell,
    TuiBadgedContent,
    TuiDataList,
    TuiFiles,
    TuiInputChip,
    TuiButtonClose,
    TuiInputDate,
    TuiCalendar,
    TuiTooltip,
    TuiCheckbox,
    TuiNotification,

    // Taiga UI Modules
    TuiTextareaModule,
    TuiInputDateModule,
    TuiInputModule,
    TuiInputPhoneModule,
    TuiInputTimeModule,
    TuiInputDateRangeModule,

    // Custom Components & Directives
    InputHeading,
    TextDirectionDirective,
    PermissionDisableDirective
  ],
  providers: [
    tuiInputPhoneInternationalOptionsProvider({
      metadata: defer(() =>
        import('libphonenumber-js/max/metadata').then(m => m.default),
      ),
    }),
    tuiDateFormatProvider({ mode: 'YMD', separator: '/' }),
  ],
})
export class FacilityDrawer implements OnInit, OnDestroy, OnChanges {
  isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;

  @HostListener('window:resize')
  onResize(): void {
    if (typeof window !== 'undefined') {
      this.isMobile = window.innerWidth <= 768;
    }
  }

  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly open = signal(false);
  protected readonly submissionStatus = signal<'idle' | 'submitting' | 'success' | 'error'>('idle');
  protected readonly submitError = signal<string | null>(null);
  protected readonly countries = getCountries();
  protected countryIsoCode = 'IN';

  // Add signal to track current currency symbol
  protected readonly currentCurrencySymbol = signal('د.إ') // Changed default to AED

  // Add signal to track current text direction
  protected readonly currentTextDirection = signal<'ltr' | 'rtl'>('ltr');

  // Tax dropdown (object-based multi-select) — mirrored from vehicle-rate drawer
  protected selectedTaxes: { id?: string; name: string; index: number; value?: number | string; mode?: 'percent' | 'fixed' | string }[] = [];
  protected taxesObjects: { id?: string; name: string; index: number; value?: number | string; mode?: 'percent' | 'fixed' | string }[] = [];
  protected readonly strings = (item: unknown) => typeof item === 'string';
  // Identity matcher for tax items to show checkmark on selected items
  protected readonly taxIdentityMatcher: TuiIdentityMatcher<{ id?: string; name: string; value?: number | string; mode?: 'percent' | 'fixed' | string }> = (tax1, tax2) => {
    if (!tax1 || !tax2) return false;
    // Match by ID if available
    if (tax1.id && tax2.id) {
      return String(tax1.id) === String(tax2.id);
    }
    // Fallback to name matching
    return tax1.name?.toLowerCase() === tax2.name?.toLowerCase();
  };

  protected readonly stringifyTax = (item: { id?: string; name: string; value?: number | string; mode?: 'percent' | 'fixed' | string } | null): string => {
    if (!item) return '';
    const name = item.name;
    const mode = (item.mode || '').toString().toLowerCase();
    const hasValue = item.value !== undefined && item.value !== null && item.value !== '';
    if (!hasValue) return name;
    const valueStr = this.formatNumeric(item.value ?? '');
    if (mode === 'percent' || mode === 'percentage') {
      return `${name} (${valueStr}%)`;
    }
    const cur = this.currentCurrencySymbol();
    return `${name} (${cur} ${valueStr})`;
  };

  // Backward-compat bindings used by current template
  protected objects: any[] = [];
  protected onTaxesChange(values: any[]): void {
    const firstLabel = Array.isArray(values) && values.length > 0 ? (values[0]?.name ?? values[0]) : '';
    this.testForm.patchValue({ taxValue: firstLabel || '' });
  }

  // Input property for editing facility data
  @Input() facilityDataForEdit: any = null;

  // Input property to restrict past dates for Start Date
  @Input() restrictPastDates: boolean = false;

  // Input property for parking ID
  @Input() parkingId: string = '';

  // Minimum date for Start Date (today)
  protected readonly minDate = TuiDay.currentLocal();

  // Signal for minimum date for End Date
  protected readonly minEndDate = signal<TuiDay | null>(null);

  // Helper to update minEndDate based on start date and restrictions
  protected updateMinEndDate(startDate: any): void {
    if (startDate instanceof TuiDay) {
      this.minEndDate.set(startDate);
    } else if (this.restrictPastDates && !this.isEditMode) {
      this.minEndDate.set(this.minDate);
    } else {
      this.minEndDate.set(null);
    }
  }

  // Property to store existing images for edit mode
  protected existingImages: string[] = [];

  // Computed property to check if we're in edit mode
  protected get isEditMode(): boolean {
    return !!this.facilityDataForEdit;
  }

  // Property to track if name field should be readonly after user interaction
  protected isNameFieldReadonly = signal(false);

  // Property to track if this is from existing service (name + unit type readonly)
  protected isFromExistingService = signal(false);

  // Properties for template compatibility
  protected selectedFacility: any = null;
  protected selectedVehicle: any = null;
  protected statusVehicle = ['Active', 'Inactive'];

  // Property to store selected service ID from dropdown
  protected selectedServiceId: string | null = null;

  // Form validation getter
  protected get isFormValid(): boolean {
    return this.testForm.valid;
  }

  /**
* Open drawer for creating new facility
*/
  public openForCreate(): void {

    // Clear any existing edit data
    this.facilityDataForEdit = null;
    this.selectedServiceId = null // Clear selected service ID

    // Reset form to default values
    this.testForm.reset();
    this.fileControl.setValue([]);
    this.rejected = [];
    this.existingImages = [] // Clear existing images
    this.isNameFieldReadonly.set(false) // Reset readonly state
    this.isFromExistingService.set(false) // Reset existing service flag

    // Set default values - use first available unit type or fallback
    const defaultUnitType = this.unitTypes.length > 0 ? this.unitTypes[0].name : 'Time Based';
    const defaultTaxType = this.taxTypes.length > 0
      ? (this.taxTypes[0].type === 'percentage' && this.taxTypes[0].rate_percent
        ? `${this.taxTypes[0].name} (${this.taxTypes[0].rate_percent}%)`
        : this.taxTypes[0].name)
      : 'GST';
    // Set default dates (today to 1 year from today)
    const today = TuiDay.currentLocal();
    const oneYearLater = today.append({ year: 1 });

    this.testForm.patchValue({
      subPlanValue: null,
      unitTypeValue: 'hourly',
      billingCycleValue: 'Monthly',
      taxValue: defaultTaxType,
      statusValue: 'Active',
      startDate: today,
      endDate: null,
      isSessionBasedValue: false
    }, { emitEvent: false });

    // Reset submission status
    this.submissionStatus.set('idle');
    this.submitError.set(null);

    // Don't set initial description - it will be set when user selects a pricing type
    this.currentUnitTypeDescription.set([]);

    // Open the drawer
    this.open.set(true);
  }

  /**
   * Open drawer for creating new facility with prefilled data
   */
  public openForCreateWithData(prefilledData: any): void {
    // Clear any existing edit data
    this.facilityDataForEdit = null;
    this.submissionStatus.set('idle');
    this.submitError.set(null);

    // Store the selected service ID if provided
    this.selectedServiceId = prefilledData.service_id || null;

    // Reset form to default values
    this.testForm.reset();
    this.fileControl.setValue([]);
    this.rejected = [];
    this.existingImages = [] // Clear existing images
    this.isNameFieldReadonly.set(false) // Reset readonly state
    this.isFromExistingService.set(false) // Reset existing service flag

    // Set default values - use first available unit type or fallback
    const defaultUnitType = this.unitTypes.length > 0 ? this.unitTypes[0].name : 'Time Based';
    const defaultTaxType = this.taxTypes.length > 0
      ? (this.taxTypes[0].type === 'percentage' && this.taxTypes[0].rate_percent
        ? `${this.taxTypes[0].name} (${this.taxTypes[0].rate_percent}%)`
        : this.taxTypes[0].name)
      : 'GST';

    // Set form values with prefilled data
    // Determine pricing type — name-based rules take priority over API-supplied value
    const prefilledName = (prefilledData.facilityName || '').toLowerCase();
    let unitTypeValue: string;
    if (prefilledName.includes('refreshment') || prefilledName.includes('restroom')) {
      unitTypeValue = 'Per Unit';
    } else if (prefilledName.includes('valet')) {
      const current = String(prefilledData.pricingType || defaultUnitType || '').toLowerCase().trim();
      unitTypeValue = (current === 'flat rate' || current === 'flat')
        ? 'Per Unit'
        : (prefilledData.pricingType || 'Time Based');
    } else if (prefilledName.includes('locker')) {
      unitTypeValue = this.lockerPricingType(prefilledData.pricingType);
    } else {
      unitTypeValue = prefilledData.pricingType || defaultUnitType;
    }

    // If prefilledData has facilityName, mark as from existing service
    const isFromExisting = !!(prefilledData.facilityName && prefilledData.facilityName.trim());
    this.isFromExistingService.set(isFromExisting);

    // Set dates from prefilled data or defaults
    let startDate: TuiDay | null = null;
    let endDate: TuiDay | null = null;

    if (prefilledData.startDate) {
      startDate = TuiDay.fromLocalNativeDate(new Date(prefilledData.startDate));
    } else {
      startDate = TuiDay.currentLocal();
    }

    if (prefilledData.endDate) {
      endDate = TuiDay.fromLocalNativeDate(new Date(prefilledData.endDate));
    } else {
      // Default end date should be null (cleared) until user selects it
      endDate = null;
    }

    // Prefer VPS/API Service Flow when provided; otherwise default Time Based → checked
    const hasApiSession =
      prefilledData.is_session_based !== undefined && prefilledData.is_session_based !== null;
    const isSessionDefault = hasApiSession
      ? !!(prefilledData.is_session_based === true || prefilledData.is_session_based === 'true' || prefilledData.is_session_based === 1)
      : (unitTypeValue?.toLowerCase().trim() === 'time based' ||
        unitTypeValue?.toLowerCase().includes('hour'));

    this.testForm.patchValue({
      nameValue: prefilledData.facilityName || '',
      descriptionValue: prefilledData.description || '',
      unitTypeValue: prefilledData.unitType || 'hourly',
      locationValue: prefilledData.location || '',
      capacityValue: prefilledData.capacity !== undefined && prefilledData.capacity !== null
        ? String(prefilledData.capacity)
        : '',
      taxValue: prefilledData.tax || defaultTaxType,
      billingCycleValue: 'Monthly',
      statusValue: prefilledData.statusValue || 'Active',
      startDate: startDate,
      endDate: endDate,
      isSessionBasedValue: isSessionDefault
    }, { emitEvent: false });

    // Set subPlanValue in setTimeout so it fires after ngOnInit subscriptions
    // ensuring name-based default (e.g. Per Unit for Refreshments/Restrooms) is not overridden
    setTimeout(() => {
      this.testForm.patchValue({ subPlanValue: unitTypeValue }, { emitEvent: false });
      const description = this.getUnitTypeDescription(unitTypeValue);
      this.currentUnitTypeDescription.set(description);
    }, 0);

    // Open the drawer
    this.open.set(true);
  }

  /**
   * Open drawer for editing existing facility
   */
  public openForEdit(facilityData: any): void {
    // Set the facility data for editing
    this.facilityDataForEdit = facilityData;
    this.selectedServiceId = null; // Clear selected service ID in edit mode
    this.submissionStatus.set('idle');
    this.submitError.set(null);

    // Populate the form with existing facility data
    this.populateFormWithFacilityData(facilityData);

    // Open the drawer
    this.open.set(true);
  }

  // Event emitter for facility creation
  @Output() facilityCreated = new EventEmitter<any>();

  protected readonly modulesList = [
    'Dashboard',
    'Parking Session',
    'Passes',
    'Facilities',
    'Reports',
    'Settings',
    'Team',
    'Reservation',
    'Payment Gateway',
  ];

  protected readonly billingCycle = ['Monthly', 'Yearly', 'Custom'];

  // Unit type options for dropdown
  protected readonly unitTypeOptions = ['hourly', 'flat'];

  /** Locker pricing is Time Based or Per Unit. Catalog Flat Rate is not a choice. */
  private lockerPricingType(pricingType: string | null | undefined): string {
    const current = String(pricingType || '').toLowerCase().trim();
    if (current === 'per unit' || current === 'unit based' || current === 'unit') {
      return 'Per Unit';
    }
    return 'Time Based';
  }

  // Get unit type labels for dropdown
  protected get subPlan(): string[] {
    const facilityName = this.testForm.get('nameValue')?.value || '';
    const nameLower = facilityName.toLowerCase();

    // Check for Locker Service
    if (nameLower.includes('locker')) {
      return this.isEditMode || this.isFromExistingService()
        ? ['Time Based', 'Per Unit']
        : ['Per Unit'];
    }

    // Check for Refreshments
    if (nameLower.includes('refreshment') || nameLower.includes('refreshments')) {
      return ['Per Unit', 'Flat Rate'];
    }

    // Check for Restrooms / Restroom Access
    if (nameLower.includes('restroom') || nameLower.includes('restrooms')) {
      return ['Per Unit', 'Flat Rate'];
    }

    // Check for Valet Parking Service
    if (nameLower.includes('valet')) {
      return ['Time Based', 'Per Unit'];
    }

    // Check for Energy Consumption or EV Charging
    if (nameLower.includes('energy') || nameLower.includes('ev') || nameLower.includes('charging')) {
      return ['Flat Rate'];
    }

    const availableUnitTypes = this.unitTypes
      .map(unit => unit.name || unit.label || unit.title || 'Unknown Unit')
      .filter(type => type.toLowerCase() !== 'energy consumption' && type.toLowerCase() !== 'energy');

    const fallbackList = availableUnitTypes.length > 0 ? availableUnitTypes : ['Time Based', 'Per Unit', 'Flat Rate'];

    // Custom facilities (create + edit): no Flat Rate; ensure Time Based is available
    const withoutFlatRate = fallbackList.filter(
      type => type.toLowerCase().trim() !== 'flat rate'
    );
    const hasTimeBased = withoutFlatRate.some(
      type => type.toLowerCase().trim() === 'time based'
    );
    return hasTimeBased ? withoutFlatRate : ['Time Based', ...withoutFlatRate];
  }


  // Dynamic currency list from service
  protected currency: string[] = [];
  protected regions: Region[] = [];

  protected modulesArray = new FormArray(
    this.modulesList.map(() => new FormControl(false))
  );

  protected testForm = new FormGroup({
    nameValue: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(50),
    ]),
    descriptionValue: new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(500)
    ]),
    subPlanValue: new FormControl<string | null>(null, Validators.required),
    unitTypeValue: new FormControl('hourly', Validators.required),
    billingCycleValue: new FormControl('Monthly', Validators.required),
    currencyValue: new FormControl(''),
    locationValue: new FormControl('', [
      Validators.minLength(0),
      Validators.maxLength(100)
    ]),
    capacityValue: new FormControl('', [
      Validators.pattern(/^\d+$/)
    ]),
    taxValue: new FormControl(''),
    statusValue: new FormControl('Active', Validators.required),
    startDate: new FormControl<TuiDay | null>(null),
    endDate: new FormControl<TuiDay | null>(null),
    isSessionBasedValue: new FormControl(false),
  });

  private subscription = new Subscription();
  private readonly facilitiesContainerKey = 'parkingAddCapacityFacilities';

  private saveFacilityToContainer(facility: any): void {
    try {
      const existing = JSON.parse(localStorage.getItem(this.facilitiesContainerKey) || '[]');
      existing.push(facility);
      localStorage.setItem(this.facilitiesContainerKey, JSON.stringify(existing));
    } catch {
      localStorage.setItem(this.facilitiesContainerKey, JSON.stringify([facility]));
    }
  }

  private getFacilitiesFromContainer(): any[] {
    try {
      return JSON.parse(localStorage.getItem(this.facilitiesContainerKey) || '[]');
    } catch {
      return [];
    }
  }

  // Unit types from API
  protected unitTypes: any[] = [];
  protected isLoadingUnits = signal(false);

  // Tax types from API
  protected taxTypes: any[] = [];
  protected isLoadingTaxes = signal(false);

  // Signal to track current unit type description
  public readonly currentUnitTypeDescription = signal<string[]>([]);

  constructor(
    private currencyService: CurrencyService,
    private apiService: ApiService,
    private notificationService: NotificationService
  ) { }

  /**
   * Fetch unit types from API
   */
  protected fetchUnitTypes(): void {
    this.isLoadingUnits.set(true);

    this.subscription.add(
      this.apiService.getUnits().subscribe({
        next: (response) => {
          if (Array.isArray(response)) {
            this.unitTypes = response;
          } else if (response?.success && Array.isArray(response.data)) {
            this.unitTypes = response.data;
          } else {
            this.unitTypes = [];
          }
          this.isLoadingUnits.set(false);
        },
        error: () => {
          this.unitTypes = [];
          this.isLoadingUnits.set(false);
        }
      })
    );
  }

  /**
   * Get description based on unit type selection
   */
  protected getUnitTypeDescription(unitType: string): string[] {
    // Handle null/undefined unitType - return empty array instead of default description
    if (!unitType || typeof unitType !== 'string') {
      return [];
    }

    const descriptions: { [key: string]: string[] } = {
      'Time Based ': [
        'Charges users based on the total time the service is used',
        'Ideal for: Vehicle, lockers, valet holding, hourly rentals, timed reservations',
        'Requires you to define the unit label (e.g., hour, fixed till 4)',
        'Use optional duration to define preset blocks (like "for 4 hours")'
      ],
      'Flat Rate': [
        'One flat price, regardless of time or quantity',
        'Ideal for services like car wash, interior cleaning, standard valet',
        'You can create multiple tiers (e.g., Basic, Premium) as separate services',
        'Quick and easy for users to understand and operators to apply'
      ],
      'Energy Consumption': [
        'Charges based on energy usage (e.g., kWh)',
        'Best for EV charging services',
        'You must define the unit label as kWh or equivalent',
        'Can be combined with idle time penalties (in the future)'
      ],
      'Per Unit': [
        'Charges based on quantity of items or sessions',
        'Ideal for helmet lockers, printed passes, or bottled water stations',
        'Use meaningful labels like helmet, unit, session',
        'Quantity is selected at the time of service usage'
      ],
      'Per Day': [
        'Charges based on the number of days the service is used',
        'Ideal for daily parking services or daily valet services',
        'You must define the unit label as day',
        'Quantity is selected at the time of service usage'
      ],
      'Hour Based': [
        'Charges based on the number of hours the service is used',
        'Ideal for hourly parking services or hourly valet services',
        'You must define the unit label as hour',
        'Quantity is selected at the time of service usage'
      ],
    };

    // Check for exact match first
    let result: string[] = [];
    if (descriptions[unitType]) {
      result = [...descriptions[unitType]];
    } else {
      // Check for partial matches (case insensitive)
      const lowerUnitType = unitType.toLowerCase();
      for (const [key, value] of Object.entries(descriptions)) {
        if (key.toLowerCase().includes(lowerUnitType) || lowerUnitType.includes(key.toLowerCase())) {
          result = [...value];
          break;
        }
      }
    }

    // Default to Time Based if no match found
    if (result.length === 0) {
      result = [...descriptions['Time Based ']];
    }

    // Add Session Based / Service Flow instruction
    const isSessionBased = this.testForm.get('isSessionBasedValue')?.value;
    if (isSessionBased) {
      result.push('Service Flow: Requires Check-in & Check-out');
    } else {
      result.push('Service Flow: One-time Usage - No check-in/check-out required');
    }

    return result;
  }

  // Get tax names for dropdown with rate information
  protected get tax(): string[] {
    const taxNames = this.taxTypes.map(tax => {
      if (tax.name) {
        // Show name with rate information
        if (tax.type === 'percentage' && tax.rate_percent) {
          return `${tax.name} (${tax.rate_percent}%)`;
        } else if (tax.type === 'fixed' && tax.rate_amount) {
          return `${tax.name} (${tax.rate_amount})`;
        } else {
          return tax.name;
        }
      }
      return 'Unknown Tax';
    });
    return taxNames;
  }

  // Expose allTaxes as objects list for the chip selector
  protected get allTaxes(): { id?: string; name: string; index: number; value?: number | string; mode?: 'percent' | 'fixed' | string }[] {
    // Filter out already selected taxes from the list
    if (!this.selectedTaxes || this.selectedTaxes.length === 0) {
      return this.taxesObjects;
    }
    return this.taxesObjects.filter(tax => {
      // Check if this tax is already selected using identity matcher
      return !this.selectedTaxes.some(selected => this.taxIdentityMatcher(tax, selected));
    });
  }

  ngOnInit(): void {
    // Fetch unit types from API
    this.fetchUnitTypes();

    // Fetch tax types from API
    this.fetchTaxTypes();

    // Subscribe to regions changes
    this.subscription.add(
      this.currencyService.regions$.subscribe(regions => {
        this.regions = regions;
        // Update currency list based on regions - show currency code and symbol
        this.currency = regions.map(region =>
          `${region.currency_code} (${region.currency_symbol})`
        );

        // Set initial currency value in new format
        const defaultRegion = regions.find(r => r.currency_code === 'INR') || regions[0];
        if (defaultRegion) {
          // Set default tax type from loaded data or fallback
          const defaultTaxType = this.taxTypes.length > 0
            ? (this.taxTypes[0].type === 'percentage' && this.taxTypes[0].rate_percent
              ? `${this.taxTypes[0].name} (${this.taxTypes[0].rate_percent}%)`
              : this.taxTypes[0].name)
            : 'GST';
          this.testForm.patchValue({
            currencyValue: `${defaultRegion.currency_code} (${defaultRegion.currency_symbol})`,
            unitTypeValue: 'hourly',
            billingCycleValue: 'Monthly',
            taxValue: defaultTaxType,
            statusValue: 'Active'
          });
          // NOTE: subPlanValue is NOT set here — it is set exclusively in openForCreate/openForCreateWithData
          // based on the facility name (e.g. Per Unit for Refreshments/Restrooms)
          this.currentCurrencySymbol.set(defaultRegion.currency_symbol);
          this.currentTextDirection.set(defaultRegion.text_direction);
        }

        // If defaultRegionControl is true, automatically select India
        if (this.defaultRegionControl.value) {
          const indiaRegion = regions.find(r => r.currency_code === 'INR');
          if (indiaRegion) {
            this.control.setValue([indiaRegion]);
          }
        }
      })
    );

    // Subscribe to current region changes
    this.subscription.add(
      this.currencyService.currentRegion$.subscribe(region => {
        if (region) {
          // Update form with current region's currency in new format
          this.testForm.patchValue({
            currencyValue: `${region.currency_code} (${region.currency_symbol})`
          });
          // Update currency symbol
          this.currentCurrencySymbol.set(region.currency_symbol);
          // Update text direction
          this.currentTextDirection.set(region.text_direction);
        }
      })
    );

    // Subscribe to currency value changes to update symbol and text direction
    this.subscription.add(
      this.testForm.get('currencyValue')?.valueChanges.subscribe(currencyValue => {
        if (currencyValue) {
          // Extract currency code from the display format "INR ()" -> "INR"
          const currencyCode = currencyValue.split(' ')[0];
          const region = this.regions.find(r => r.currency_code === currencyCode);
          if (region) {
            this.currentCurrencySymbol.set(region.currency_symbol);
            this.currentTextDirection.set(region.text_direction);
          }
        }
      })
    );

    // Subscribe to pricing type changes to update description + Service Flow.
    // Time Based → always on; Unit-based → optional (default off).
    // Helmet Locker / Valet — do not override Service Flow from pricing type.
    this.subscription.add(
      this.testForm.get('subPlanValue')?.valueChanges.subscribe((unitType) => {
        if (unitType) {
          if (!this.isProtectedFacilityService()) {
            const type = unitType.toLowerCase().trim();
            const isTimeBased = type === 'time based' || type === 'hour based';
            const isUnitBased = type === 'per unit' || type === 'unit based';
            const sessionCtrl = this.testForm.get('isSessionBasedValue');
            if (isTimeBased) {
              sessionCtrl?.setValue(true, { emitEvent: false });
              sessionCtrl?.disable({ emitEvent: false });
            } else {
              sessionCtrl?.enable({ emitEvent: false });
              if (isUnitBased) {
                // Optional: default off; admin can still enable Service Flow
                sessionCtrl?.setValue(false, { emitEvent: false });
              }
            }
          }
          const description = this.getUnitTypeDescription(unitType);
          this.currentUnitTypeDescription.set(description);
        } else {
          // Clear description if no pricing type is selected
          this.currentUnitTypeDescription.set([]);
        }
      })
    );

    // Subscribe to session-based toggle changes to update description
    this.subscription.add(
      this.testForm.get('isSessionBasedValue')?.valueChanges.subscribe(() => {
        const unitType = this.testForm.get('subPlanValue')?.value;
        if (unitType) {
          const description = this.getUnitTypeDescription(unitType);
          this.currentUnitTypeDescription.set(description);
        }
      })
    );

    // Handle default region control changes
    this.defaultRegionControl.valueChanges.subscribe(value => {
      if (value) {
        // When default region is enabled, select India and disable the control
        const indiaRegion = this.regions.find(r => r.currency_code === 'INR');
        if (indiaRegion) {
          this.control.setValue([indiaRegion]);
        }
        this.control.disable();
      } else {
        // When default region is disabled, clear selection and enable the control
        this.control.setValue([]);
        this.control.enable();
      }
    });

    // Subscribe to start date changes to update end date restriction
    this.subscription.add(
      this.testForm.get('startDate')?.valueChanges.subscribe((startDate) => {
        this.updateMinEndDate(startDate);
      })
    );

    // Initial update of minEndDate
    this.updateMinEndDate(this.testForm.get('startDate')?.value);

    // Initialize the control state based on defaultRegionControl value
    if (this.defaultRegionControl.value) {
      const indiaRegion = this.regions.find(r => r.currency_code === 'INR');
      if (indiaRegion) {
        this.control.setValue([indiaRegion]);
      }
      this.control.disable();
    } else {
      this.control.enable();
    }

    // Initialize table with default pricing type
    // this.updateTable() // Removed updateTable call
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['facilityDataForEdit'] && changes['facilityDataForEdit'].currentValue) {
      this.populateFormWithFacilityData(changes['facilityDataForEdit'].currentValue);
    }
    // Update minEndDate when inputs change (e.g. restrictPastDates or facilityDataForEdit)
    this.updateMinEndDate(this.testForm.get('startDate')?.value);
  }

  /**
   * Populate form with facility data for editing — show API values as-is
   */
  protected populateFormWithFacilityData(facilityData: any): void {
    if (!facilityData) return;

    // Prefer explicit pricingType from API/container; fall back to unit type name fields
    const apiPricingType =
      facilityData.pricingType ||
      facilityData.unit?.name ||
      facilityData.unit_name ||
      facilityData.unitType?.name ||
      facilityData.unit_type?.name ||
      'Time Based';

    let unitTypeValue = apiPricingType;
    const matchingUnitType = this.unitTypes.find(unit =>
      unit.name === apiPricingType ||
      unit.label === apiPricingType ||
      unit.title === apiPricingType
    );
    if (matchingUnitType) {
      unitTypeValue = matchingUnitType.name || matchingUnitType.label || matchingUnitType.title;
    }

    const facilityNameLower = String(facilityData.facilityName || facilityData.name || '').toLowerCase();
    if (facilityNameLower.includes('valet')) {
      const current = String(unitTypeValue || '').toLowerCase().trim();
      if (current === 'flat rate' || current === 'flat') {
        unitTypeValue = 'Per Unit';
      }
    }
    if (facilityNameLower.includes('locker')) {
      unitTypeValue = this.lockerPricingType(unitTypeValue);
    }

    // Handle tax data from service_taxes array or fallback to simple tax string
    let taxTypeValue = facilityData.tax || '';

    // If we have service_taxes array, extract tax information from it
    if (facilityData.service_taxes && Array.isArray(facilityData.service_taxes) && facilityData.service_taxes.length > 0) {
      const taxes = facilityData.service_taxes.map((serviceTax: any) => {
        const tax = serviceTax.taxes || serviceTax.tax || serviceTax;
        if (!tax) return null;
        if (tax.type === 'percentage' || tax.type === 'percent') {
          return `${tax.name} (${tax.rate_percent}%)`;
        } else if (tax.type === 'amount' || tax.type === 'fixed') {
          return `${tax.name} (${tax.rate_amount})`;
        }
        return tax.name;
      }).filter(Boolean);
      taxTypeValue = taxes.join(', ');
    } else {
      // Fallback to old logic for backward compatibility
      const matchingTaxType = this.taxTypes.find(tax => {
        if (tax.name === facilityData.tax) return true;

        // Also check if the facility data contains the full tax object
        if (facilityData.taxTypeData && facilityData.taxTypeData.id === tax.id) return true;

        return false;
      });
      if (matchingTaxType) {
        // Use the display format that matches our getter
        if (matchingTaxType.type === 'percentage' && matchingTaxType.rate_percent) {
          taxTypeValue = `${matchingTaxType.name} (${matchingTaxType.rate_percent}%)`;
        } else if (matchingTaxType.type === 'fixed' && matchingTaxType.rate_amount) {
          taxTypeValue = `${matchingTaxType.name} (${matchingTaxType.rate_amount})`;
        } else {
          taxTypeValue = matchingTaxType.name;
        }
      }
    }

    // Handle dates from facility data (support API field aliases)
    let startDate: TuiDay | null = null;
    let endDate: TuiDay | null = null;

    const rawStart = facilityData.startDate || facilityData.effective_start || facilityData.effectiveStart || null;
    const rawEnd = facilityData.endDate || facilityData.effective_end || facilityData.effectiveEnd || null;

    if (rawStart) {
      const parsedStart = new Date(rawStart);
      if (!Number.isNaN(parsedStart.getTime())) {
        startDate = TuiDay.fromLocalNativeDate(parsedStart);
      }
    }

    if (rawEnd) {
      const parsedEnd = new Date(rawEnd);
      if (!Number.isNaN(parsedEnd.getTime())) {
        endDate = TuiDay.fromLocalNativeDate(parsedEnd);
      }
    }

    // Service Flow from VPS/API — do not override with pricing type
    const rawSession = facilityData.is_session_based ?? facilityData.isSessionBased;
    const isSessionBased = rawSession === true || rawSession === 'true' || rawSession === 1 || rawSession === '1';

    const capacityRaw = facilityData.capacity;
    const capacityValue =
      capacityRaw !== undefined && capacityRaw !== null && capacityRaw !== ''
        ? String(capacityRaw)
        : '';

    const statusRaw = facilityData.statusValue ?? facilityData.status;
    let statusValue = 'Active';
    if (statusRaw === false || statusRaw === 'Inactive' || statusRaw === 'inactive') {
      statusValue = 'Inactive';
    } else if (statusRaw === true || statusRaw === 'Active' || statusRaw === 'active') {
      statusValue = 'Active';
    } else if (typeof statusRaw === 'string' && statusRaw.trim()) {
      statusValue = statusRaw;
    }

    // Populate form fields with facility data (emitEvent: false so subscriptions don't overwrite API values)
    this.testForm.patchValue({
      nameValue: facilityData.facilityName || facilityData.name || '',
      descriptionValue: facilityData.description || '',
      unitTypeValue: facilityData.unitType || 'hourly',
      locationValue: facilityData.location || '',
      capacityValue,
      taxValue: taxTypeValue,
      statusValue,
      startDate,
      endDate,
      isSessionBasedValue: isSessionBased
    }, { emitEvent: false });

    // Set subPlanValue after a tick so the subPlan getter has recalculated
    // with the correct nameValue (important for locker: Time Based must be in list)
    setTimeout(() => {
      this.testForm.patchValue({ subPlanValue: unitTypeValue }, { emitEvent: false });
      const description = this.getUnitTypeDescription(unitTypeValue);
      this.currentUnitTypeDescription.set(description);
      this.updateMinEndDate(startDate);
    }, 0);

    // Sync selected taxes after form is populated
    // Use setTimeout to ensure taxes are loaded first
    setTimeout(() => {
      this.syncSelectedTaxesFromData();
    }, 100);

    // Handle uploaded images if they exist
    if (facilityData.uploadedImages && facilityData.uploadedImages.length > 0) {
      // Store the existing image URLs for display
      this.existingImages = [...facilityData.uploadedImages];
      // Clear file control since we're showing existing images
      this.fileControl.setValue([]);
    } else if (facilityData.media_assets && facilityData.media_assets.length > 0) {
      this.existingImages = [...facilityData.media_assets];
      this.fileControl.setValue([]);
    } else {
      this.existingImages = [];
    }

    // Open the drawer
    this.open.set(true);
  }

  /**
   * Close drawer and clear edit data
   */
  protected closeDrawer(): void {
    this.open.set(false);
    this.submissionStatus.set('idle');
    this.submitError.set(null);
    // Clear edit data when closing
    this.facilityDataForEdit = null;
    this.selectedServiceId = null;
    this.existingImages = [];
  }

  /**
   * Test method to manually trigger unit types fetch (for debugging)
   */












  /**
   * Fetch tax types from API
   */
  protected fetchTaxTypes(): void {
    this.isLoadingTaxes.set(true);

    const orgId = this.apiService.getCurrentOrganizationId();
    // Get current parking ID (from route or API service)
    const parkingId = this.apiService.getCurrentParkingId();

    this.subscription.add(
      this.apiService.getTaxes(orgId || undefined).subscribe({
        next: (response) => {
          let list: any[] = [];

          // Handle direct array response (no success/data wrapper)
          if (Array.isArray(response)) {
            list = response;
          } else if (response.success && response.data) {
            // Handle wrapped response structure
            list = Array.isArray(response.data) ? response.data : [];
          }

          // Filter taxes based on parking context
          if (list.length > 0) {
            const filteredList = this.filterTaxesByParkingContext(list, parkingId);
            // Filter out invalid tax entries (where name is null)
            this.taxTypes = filteredList.filter(tax => tax.name && tax.status !== false);
            this.taxesObjects = filteredList.map((t: any, index: number) => this.toTaxItem(t, index));
          } else {
            this.taxTypes = [];
            this.taxesObjects = [];
          }
          this.isLoadingTaxes.set(false);

          // Sync selected taxes after taxes are loaded (for edit mode)
          if (this.facilityDataForEdit) {
            setTimeout(() => {
              this.syncSelectedTaxesFromData();
            }, 50);
          }
        },
        error: (error) => {
          this.taxTypes = [];
          this.taxesObjects = [];
          this.isLoadingTaxes.set(false);
        }
      })
    );
  }


  // Format numeric values consistently with vehicle-rate drawer
  private formatNumeric(value: number | string): string {
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value ?? '');
    if (Number.isInteger(num)) return String(num);
    let s = num.toString();
    if (s.includes('e') || s.includes('E')) s = num.toFixed(6);
    s = s.replace(/\.?0+$/, '');
    return s;
  }

  // Sync chip selections into the reactive form (keep backward compatibility)
  protected ngModelChangeForTaxes(values: { id?: string; name: string; index: number; value?: number | string; mode?: 'percent' | 'fixed' | string }[]): void {
    this.selectedTaxes = values ?? [];
    const labels = this.selectedTaxes.map(v => v.name);
    // For backward compatibility with single string taxValue, set first or empty
    this.testForm.patchValue({ taxValue: labels[0] ?? '' });
  }

  private syncSelectedTaxesFromData(): void {
    if (!this.taxesObjects?.length) return;

    let selectedTaxes: any[] = [];

    // First, try to get taxes from service_taxes array if available
    if (this.facilityDataForEdit?.service_taxes && Array.isArray(this.facilityDataForEdit.service_taxes)) {
      selectedTaxes = this.facilityDataForEdit.service_taxes.map((serviceTax: any) => {
        const tax = serviceTax.taxes;
        // Find matching tax object in taxesObjects
        return this.taxesObjects.find(taxObj =>
          taxObj.name.toLowerCase() === tax.name.toLowerCase() ||
          taxObj.id === tax.id
        );
      }).filter(Boolean);
    }

    // If no taxes found from service_taxes, fallback to form control value
    if (selectedTaxes.length === 0) {
      const taxValue = this.testForm.get('taxValue')?.value;
      if (taxValue) {
        // Handle comma-separated tax values (e.g., "VAT (10%), VAT (20%)")
        const taxNames = taxValue.split(',').map((name: string) => name.trim());
        selectedTaxes = taxNames.map(taxName => {
          return this.taxesObjects.find(tax =>
            tax.name.toLowerCase() === taxName.toLowerCase() ||
            this.stringifyTax(tax).toLowerCase() === taxName.toLowerCase()
          );
        }).filter(Boolean);
      }
    }

    if (selectedTaxes.length > 0) {
      this.selectedTaxes = selectedTaxes;
    }
  }

  private toTaxItem(raw: any, index: number): { id?: string; name: string; index: number; value?: number | string; mode?: 'percent' | 'fixed' | string } {
    const name = raw?.name ?? raw?.tax_name ?? String(raw);
    const modeRaw = raw?.type ?? raw?.tax_type ?? (raw?.rate_percent != null ? 'percentage' : (raw?.rate_amount != null ? 'fixed' : undefined));
    const mode = typeof modeRaw === 'string' ? modeRaw.toLowerCase() : modeRaw;
    const value = mode === 'percentage' ? (raw?.rate_percent ?? raw?.percentage ?? raw?.value) : (raw?.rate_amount ?? raw?.amount ?? raw?.value);
    const id = raw?.id ?? raw?.org_tax_id ?? raw?.taxes?.id;
    return { id, name, index, value, mode };
  }

  /**
   * Filter taxes based on parking context
   * If in parking context: show org-level taxes + parking-level taxes for that parking
   * If in org context: show all taxes
   */
  private filterTaxesByParkingContext(taxes: any[], parkingId: string | null): any[] {
    // If not in parking context, return all taxes
    if (!parkingId || !parkingId.trim()) {
      return taxes;
    }

    // Filter taxes: show org-level (parking_Id: null) + parking-level (matching parking ID)
    const filtered = taxes.filter((tax: any) => {
      const taxParkingId = tax.parking_Id || tax.parking_id;

      // Show org-level taxes (parking_Id is null)
      if (!taxParkingId || taxParkingId === null || taxParkingId === 'null' || taxParkingId === '') {
        return true;
      }

      // Show parking-level taxes only if they match current parking ID
      return taxParkingId === parkingId;
    });

    console.log('Filtered taxes for parking context (facility-drawer):', {
      parkingId,
      totalTaxes: taxes.length,
      filteredTaxes: filtered.length
    });

    return filtered;
  }

  protected hasVisibleErrors(controlName: string): boolean {
    const control = this.testForm.get(controlName);
    return !!control?.errors && (control.touched || control.dirty);
  }

  /**
   * Get custom error message for form fields with specific messages for each control
   */
  protected getErrorMessage(controlName: string): string {
    const control = this.testForm.get(controlName);
    if (!control?.errors || !control.touched) return '';

    const errors = control.errors;

    // Required field errors with custom messages
    if (errors['required']) {
      switch (controlName) {
        case 'nameValue':
          return 'Facility name is required';
        case 'descriptionValue':
          return 'Please provide a description for this facility';
        case 'locationValue':
          return 'Location is required to help operators find the facility';
        case 'capacityValue':
          return 'Capacity limit is required';
        case 'taxValue':
          return 'Please select at least one tax option';
        case 'statusValue':
          return 'Please select a status';
        case 'subPlanValue':
          return 'Please select a pricing type';
        default:
          return 'This field is required';
      }
    }

    // Minimum length errors
    if (errors['minlength']) {
      const requiredLength = errors['minlength'].requiredLength;
      switch (controlName) {
        case 'nameValue':
          return `Facility name must be at least ${requiredLength} characters long`;
        case 'descriptionValue':
          return `Description should be at least ${requiredLength} characters to be meaningful`;
        case 'locationValue':
          return `Location should be at least ${requiredLength} characters (e.g., "Ground Floor - Section A")`;
        default:
          return `Minimum ${requiredLength} characters required`;
      }
    }

    // Maximum length errors
    if (errors['maxlength']) {
      const requiredLength = errors['maxlength'].requiredLength;
      switch (controlName) {
        case 'nameValue':
          return `Facility name cannot exceed ${requiredLength} characters`;
        case 'descriptionValue':
          return `Description is too long. Maximum ${requiredLength} characters allowed`;
        case 'locationValue':
          return `Location cannot exceed ${requiredLength} characters`;
        default:
          return `Maximum ${requiredLength} characters allowed`;
      }
    }

    // Minimum value errors
    if (errors['min']) {
      return `Minimum value is ${errors['min'].min}`;
    }

    // Maximum value errors
    if (errors['max']) {
      return `Maximum value is ${errors['max'].max}`;
    }

    // Pattern validation errors
    if (errors['pattern']) {
      switch (controlName) {
        case 'nameValue':
          return 'Only letters, numbers, spaces, hyphens and underscores are allowed';
        case 'priceAmountValue':
          return 'Please enter a valid price (e.g., 10.50 or 100)';
        case 'capacityValue':
          return 'Please enter a valid whole number';
        default:
          return 'Invalid format';
      }
    }

    return 'Invalid input';
  }

  protected isFieldValid(controlName: string): boolean {
    const control = this.testForm.get(controlName);
    return !!(control?.valid && control.touched);
  }

  protected isFieldInvalid(controlName: string): boolean {
    const control = this.testForm.get(controlName);
    return !!(control?.invalid && control.touched);
  }

  protected statusText(): string {
    const status = this.statusTextControl.value;
    return typeof status === 'boolean'
      ? status
        ? 'Pricing plan is live'
        : 'Pricing plan is draft'
      : 'Pricing plan status unknown';
  }

  protected statusTextYesNo(): string {
    const status = this.statusTextControl.value;
    return typeof status === 'boolean' ? (status ? 'Yes' : 'No') : 'Unknown';
  }

  protected readonly control = new FormControl<Region[]>([]);

  protected readonly stringify: TuiStringHandler<Region | TuiContext<Region>> = (item) =>
    'name' in item ? item.name : item.$implicit.name;

  protected readonly identityMatcher: TuiIdentityMatcher<Region> = (hero1, hero2) =>
    hero1.id === hero2.id;

  protected visibilityControl = new FormControl(false);
  protected billingScopeControl = new FormControl(false);
  protected defaultRegionControl = new FormControl<boolean | null>(true);
  protected billingVisibilityControl = new FormControl(true);
  protected statusTextControl = new FormControl(true);
  protected dateRangeControl = new FormControl(
    new TuiDayRange(new TuiDay(2024, 0, 1), new TuiDay(2024, 11, 31))
  );

  // File upload functionality with multiple files support
  protected readonly fileControl = new FormControl<File[]>([], [this.maxFilesLength(5)]);

  protected readonly accepted$ = this.fileControl.valueChanges.pipe(
    map(() => tuiFilesAccepted(this.fileControl)),
  );

  protected rejected: readonly File[] = [];

  protected onReject(files: readonly File[]): void {
    this.rejected = Array.from(new Set(this.rejected.concat(files)));
  }

  protected onRemove(file: File): void {
    this.rejected = this.rejected.filter((rejected) => rejected !== file);
    this.fileControl.setValue(
      this.fileControl.value?.filter((current) => current !== file) ?? [],
    );
  }

  /**
   * Remove existing image from edit mode
   */
  protected onRemoveExistingImage(imageIndex: number): void {
    this.existingImages.splice(imageIndex, 1);
  }

  /**
   * Get file name from image URL
   */
  protected getImageFileName(imageUrl: string): string {
    try {
      // Try to extract filename from URL
      const url = new URL(imageUrl);
      const pathname = url.pathname;
      const filename = pathname.split('/').pop() || 'image.jpg';
      return filename;
    } catch {
      // If URL parsing fails, try to extract from blob URL
      if (imageUrl.startsWith('blob:')) {
        return 'uploaded-image.jpg';
      }
      // For other cases, return a default name
      return 'facility-image.jpg';
    }
  }

  /**
   * Get file size for image (estimated)
   */
  protected getImageFileSize(imageUrl: string): string {
    // Since we don't have actual file size for existing images,
    // we'll return an estimated size based on image type
    if (imageUrl.includes('.png')) {
      return '~200 KiB';
    } else if (imageUrl.includes('.jpg') || imageUrl.includes('.jpeg')) {
      return '~150 KiB';
    } else if (imageUrl.includes('.gif')) {
      return '~300 KiB';
    } else {
      return '~100 KiB';
    }
  }

  protected maxFilesLength(maxLength: number): ValidatorFn {
    return ({ value }: AbstractControl) =>
      value && value.length > maxLength
        ? {
          maxLength: new TuiValidationError(
            'Error: maximum limit - 5 files for upload',
          ),
        }
        : null;
  }

  /**
   * Get currency symbol for the given currency code
   */
  protected getCurrencySymbol(currencyCode: string): string {
    const region = this.regions.find(r => r.currency_code === currencyCode);
    return region?.currency_symbol || 'د.إ' // Changed default to AED
  }

  /**
   * Get current region based on selected currency
   */
  protected getCurrentRegion(): Region | null {
    const currencyValue = this.testForm.get('currencyValue')?.value;
    if (currencyValue) {
      const currencyCode = currencyValue.split(' ')[0];
      return this.regions.find(r => r.currency_code === currencyCode) || null;
    }
    return null;
  }

  /**
   * Check if current region is RTL
   */
  protected isCurrentRegionRTL(): boolean {
    const region = this.getCurrentRegion();
    return region?.text_direction === 'rtl';
  }

  /**
   * Get current locale for Angular Currency Pipe
   */
  protected getCurrentLocale(): string {
    const region = this.getCurrentRegion();
    return region?.locale_code || 'en-IN';
  }

  /**
   * Get current currency code for Angular Currency Pipe
   */
  protected getCurrentCurrencyCode(): string {
    const region = this.getCurrentRegion();
    return region?.currency_code || '';
  }

  /**
   * Get the currently selected tax ID from the form
   */
  protected getSelectedTaxId(): string | null {
    const taxValue = this.testForm.get('taxValue')?.value;
    if (!taxValue) return null;

    const selectedTaxType = this.taxTypes.find(tax => {
      const displayName = tax.type === 'percentage' && tax.rate_percent
        ? `${tax.name} (${tax.rate_percent}%)`
        : tax.type === 'fixed' && tax.rate_amount
          ? `${tax.name} (${tax.rate_amount})`
          : tax.name;

      return displayName === taxValue;
    });

    return selectedTaxType?.id || null;
  }

  /**
   * Debug method to show selected tax ID
   */
  protected showSelectedTaxId(): void {
    const selectedTaxId = this.getSelectedTaxId();
    //console.log('Selected Tax ID:', selectedTaxId);
    //console.log('Current tax value:', this.testForm.get('taxValue')?.value);
    //console.log('Available tax types:', this.taxTypes);
  }

  /**
   * Format currency using CurrencyService
   */
  protected formatCurrencyWithService(amount: number): string {
    const region = this.getCurrentRegion();
    return this.currencyService.formatCurrency(amount, region || undefined);
  }

  /**
   * Get currency prefix for LTR regions
   * For LTR (Left-to-Right) languages like English, Hindi, etc.,
   * currency symbols are displayed before the amount (e.g., $100, 100)
   */
  protected getCurrencyPrefix(): string {
    return this.currentTextDirection() === 'ltr' ? ' ' + this.currentCurrencySymbol() : '';
  }

  /**
   * Get currency suffix for RTL regions
   * For RTL (Right-to-Left) languages like Arabic, Hebrew, etc.,
   * currency symbols are displayed after the amount (e.g., 100 د.إ, 100 ₪)
   */
  protected getCurrencySuffix(): string {
    return this.currentTextDirection() === 'rtl' ? ' ' + this.currentCurrencySymbol() : '';
  }

  protected sessionBasedStatusText(): string {
    const isSessionBased = this.testForm.get('isSessionBasedValue')?.value;
    return isSessionBased
      ? 'Requires Check-in & Check-out'
      : 'One-time Usage';
  }

  protected sessionBasedDescriptionText(): string {
    const isSessionBased = this.testForm.get('isSessionBasedValue')?.value;
    return isSessionBased
      ? 'Use this for services that involve an active session, such as parking, valet, EV charging, or locker usage.'
      : 'Use this for services that are completed in a single action without tracking duration.';
  }

  protected sessionBasedKnowMoreUrl(): string {
    return 'https://sistem.app/help-center/documents/what-are-facilities/';
  }

  protected sessionBasedYesNoText(): string {
    const isSessionBased = this.testForm.get('isSessionBasedValue')?.value;
    return isSessionBased ? 'Yes' : 'No';
  }

  /** Helmet Locker / Valet Parking Service — leave existing Service Flow behavior untouched. */
  protected isProtectedFacilityService(): boolean {
    const name = String(this.testForm.get('nameValue')?.value || '').toLowerCase();
    return name.includes('helmet') || name.includes('valet');
  }

  /** Time Based always requires Service Flow (toggle locked), except protected services. */
  protected isServiceFlowLocked(): boolean {
    if (this.isProtectedFacilityService()) return false;
    const type = String(this.testForm.get('subPlanValue')?.value || '').toLowerCase().trim();
    return type === 'time based' || type === 'hour based';
  }

  // Form submission method
  protected onSubmit(): void {
    if (this.submissionStatus() === 'submitting') {
      return;
    }

    this.submitError.set(null);

    // Mark all fields as touched to show validation errors
    Object.keys(this.testForm.controls).forEach(key => {
      const control = this.testForm.get(key);
      control?.markAsTouched();
    });

    // Also mark file control as touched (optional)
    this.fileControl.markAsTouched();

    // Check if form is valid (file is optional)
    if (this.testForm.valid) {
      this.submissionStatus.set('submitting');
      // getRawValue includes disabled controls (e.g. Service Flow locked for Time Based)
      const formData = this.testForm.getRawValue();

      // Time Based (non-protected): always persist Service Flow on
      if (this.isServiceFlowLocked()) {
        formData.isSessionBasedValue = true;
      }

      // Handle images: combine existing images with new uploaded files
      const uploadedImages: string[] = [];

      // Add existing images first
      if (this.existingImages.length > 0) {
        uploadedImages.push(...this.existingImages);
      }

      // Add new uploaded files (as object URLs for preview)
      if (this.fileControl.value && this.fileControl.value.length > 0) {
        this.fileControl.value.forEach(file => {
          if (file instanceof File) {
            uploadedImages.push(URL.createObjectURL(file));
          }
        });
      }

      // Find the selected unit type object from API data
      let selectedUnitType = this.unitTypes.find(unit =>
        unit.name === formData.subPlanValue ||
        unit.label === formData.subPlanValue ||
        unit.title === formData.subPlanValue
      );

      // Fallback: If "Energy" is selected but not found (because unit is "Energy Consumption"), try finding "Energy Consumption"
      if (!selectedUnitType && formData.subPlanValue === 'Energy') {
        selectedUnitType = this.unitTypes.find(unit =>
          unit.name === 'Energy Consumption' ||
          unit.label === 'Energy Consumption' ||
          unit.title === 'Energy Consumption'
        );
      }

      // Find the selected tax type object from API data
      const selectedTaxType = this.taxTypes.find(tax => {
        // Check if the display name matches
        const displayName = tax.type === 'percentage' && tax.rate_percent
          ? `${tax.name} (${tax.rate_percent}%)`
          : tax.type === 'fixed' && tax.rate_amount
            ? `${tax.name} (${tax.rate_amount})`
            : tax.name;

        return displayName === formData.taxValue;
      });

      // Log warning if no tax type found
      if (!selectedTaxType) {
        console.warn('No matching tax type found for:', formData.taxValue);
        //console.log('Available tax types:', this.taxTypes);
      }

      // Convert TuiDay to ISO string for dates
      const startDateValue = formData.startDate ? formData.startDate.toLocalNativeDate().toISOString() : null;
      const endDateValue = formData.endDate ? formData.endDate.toLocalNativeDate().toISOString() : null;

      // Create a clean object with only the relevant form data
      const submissionData = {
        isEdit: !!this.facilityDataForEdit,
        facilityName: formData.nameValue,
        description: formData.descriptionValue,
        pricingType: formData.subPlanValue,
        unitType: formData.unitTypeValue,
        unitTypeData: selectedUnitType,
        location: formData.locationValue,
        capacity: formData.capacityValue,
        tax: formData.taxValue,
        taxTypeData: selectedTaxType,
        priceAmount: 0,
        uploadedImages: uploadedImages,
        startDate: startDateValue,
        endDate: endDateValue,
        is_session_based: formData.isSessionBasedValue || false,
        originalData: this.facilityDataForEdit, // Keep original data for reference
      };

      // Get organization ID and parking ID from localStorage
      const orgId = this.getOrganizationIdFromLocalStorage();
      const parkingId = this.parkingId || this.apiService.getCurrentParkingId();
      const orgTaxId = this.getOrganizationTaxId();

      // Collect selected tax IDs as array for org_tax_id
      const orgTaxIds: string[] = Array.from(new Set((this.selectedTaxes || [])
        .map(t => String(t.id || '').trim())
        .filter(Boolean)));

      // Get existing service ID from multiple sources (priority order)
      // Filter out empty strings and only keep valid IDs
      const existingServiceId = [
        this.facilityDataForEdit?.id,
        this.facilityDataForEdit?.service_id,
        this.selectedServiceId, // Service ID from dropdown selection
        selectedUnitType?.service_id,
      ].find(id => id && String(id).trim().length > 0) || null;

      // Prepare service payload exactly as requested
      const servicePayload: any = {
        name: formData.nameValue || '',
        slug: (formData.nameValue || ''),
        description: formData.descriptionValue || '',
        service_type: 'facility',
        unit_type_id: selectedUnitType?.id || null,
        capacity: parseInt(formData.capacityValue || '0'),
        media_assets: null,
        status: formData.statusValue === 'Active',
        org_id: orgId,
        parking_id: parkingId,
        org_tax_ids: orgTaxIds,
        exisitng_service_id: existingServiceId,
        effective_start: startDateValue,
        is_session_based: formData.isSessionBasedValue || false,
        selectedTaxes: this.selectedTaxes, // Include complete tax items
      };

      // Only add effective_end if it has a value (don't send null or default)
      if (endDateValue) {
        servicePayload.effective_end = endDateValue;
      }

      if (!this.isEditMode) {
        // Create new facility via API (no images here)
        this.apiService.createService(servicePayload).subscribe({
          next: async (response) => {
            const createdId = (response as any)?.service?.id || (response as any)?.data?.id || (response as any)?.id || (response as any)?.service_id || null;
            // If files were selected, upload them to /service/media-asse
            const selectedFiles = (this.fileControl.value || []).filter(f => f instanceof File) as File[];
            if (selectedFiles.length > 0) {
              try {
                const uploadResponse = await this.apiService.uploadServiceMediaAssets({
                  service_id: createdId,
                  org_id: this.apiService.getCurrentOrganizationId() || undefined,
                  parking_id: this.apiService.getCurrentParkingId() || undefined,
                  files: selectedFiles
                }).toPromise();
                console.log('Upload successful:', uploadResponse);
              } catch (e) {
                console.error('Upload failed:', e);
                // Swallow upload error to not block creation UX; could add notification if needed
              }
            } else {
              console.log('️ No files to upload');
            }

            const emitWithFacilities = (facilities: any[]) => {
              this.open.set(false);
              this.submissionStatus.set('idle');
              this.submitError.set(null);
              this.notificationService.showSuccess(this.facilityDataForEdit ? 'Facility updated successfully!' : 'Facility created successfully!');
              this.facilityCreated.emit({
                ...submissionData,
                apiResponse: response,
                serviceId: createdId,
                serviceData: servicePayload,
                containerFacilities: facilities,
                uploadedImages: uploadedImages
              });
            };

            if (createdId) {
              // Fetch freshly created service then fetch full list to sync container
              this.apiService.getService(createdId).subscribe({
                next: () => {
                  this.apiService.getAllServices({ org_id: orgId }).subscribe({
                    next: (list) => {
                      const facilities = Array.isArray((list as any)?.data) ? (list as any).data : (Array.isArray(list) ? list : []);
                      emitWithFacilities(facilities);
                    },
                    error: () => emitWithFacilities([]),
                  });
                },
                error: () => {
                  // Fall back to list even if single fetch fails
                  this.apiService.getAllServices({ org_id: orgId }).subscribe({
                    next: (list) => {
                      const facilities = Array.isArray((list as any)?.data) ? (list as any).data : (Array.isArray(list) ? list : []);
                      emitWithFacilities(facilities);
                    },
                    error: () => emitWithFacilities([]),
                  });
                }
              });
            } else {
              // If API did not return id, fetch list directly
              this.apiService.getAllServices({ org_id: orgId }).subscribe({
                next: (list) => {
                  const facilities = Array.isArray((list as any)?.data) ? (list as any).data : (Array.isArray(list) ? list : []);
                  emitWithFacilities(facilities);
                },
                error: () => emitWithFacilities([]),
              });
            }
          },
          error: (error) => {
            const errorMsg = extractApiError(error, 'An error occurred while saving facility.');
            this.submitError.set(errorMsg);
            this.submissionStatus.set('error');
          }
        });
      } else {
        // Edit mode: use API call with the specified payload structure
        const editPayload: any = {
          name: formData.nameValue || '',
          description: formData.descriptionValue,
          parking_id: parkingId,
          org_id: orgId,
          id: this.facilityDataForEdit?.id || this.facilityDataForEdit?.service_id,
          service_type: "facility",
          capacity: parseInt(formData.capacityValue || '0'),
          status: formData.statusValue === 'Active',
          org_tax_ids: orgTaxIds,
          unit_type_id: selectedUnitType?.id || null,
          effective_start: startDateValue,
          is_session_based: formData.isSessionBasedValue || false,
          selectedTaxes: this.selectedTaxes, // Include complete tax items
        };

        // Only add effective_end if it has a value (don't send null or default)
        if (endDateValue) {
          editPayload.effective_end = endDateValue;
        }

        this.apiService.updateService(editPayload.id, editPayload).subscribe({
          next: async (response) => {

            // Handle image uploads for edit mode - use the comprehensive approach
            const selectedFiles = (this.fileControl.value || []).filter(f => f instanceof File) as File[];

            if (selectedFiles.length > 0 || this.existingImages.length > 0) {

              try {
                const uploadResponse = await this.apiService.updateServiceMediaAssets({
                  service_id: editPayload.id,
                  org_id: orgId || undefined,
                  parking_id: parkingId || undefined,
                  files: selectedFiles,
                  existing_urls: this.existingImages
                });
                console.log('Media update successful for edit:', uploadResponse);
              } catch (e) {
                console.error('Media update failed for edit:', e);
                // Swallow upload error to not block update UX
              }
            }

            const emitWithFacilities = (facilities: any[]) => {
              this.open.set(false);
              this.submissionStatus.set('idle');
              this.submitError.set(null);
              this.notificationService.showSuccess(this.facilityDataForEdit ? 'Facility updated successfully!' : 'Facility created successfully!');
              this.facilityCreated.emit({
                ...submissionData,
                apiResponse: response,
                serviceId: editPayload.id,
                serviceData: editPayload,
                containerFacilities: facilities,
                uploadedImages: uploadedImages
              });
            };

            emitWithFacilities([]);
          },
          error: (error) => {
            console.error('Service update failed:', error);
            const errorMsg = extractApiError(error, 'Failed to update facility.');
            this.submitError.set(errorMsg);
            this.submissionStatus.set('error');
          }
        });
      }

    } else {
      this.testForm.markAllAsTouched();
      this.submissionStatus.set('idle');
    }
  }

  /**
   * Set organization ID in localStorage (for debugging)
   */
  protected setOrganizationIdInLocalStorage(orgId: string): void {
    sessionStorage.setItem('organization_id', orgId);
    //console.log('Organization ID set in localStorage:', orgId);
  }

  /**
   * Set the correct organization ID from your error message
   */
  protected setCorrectOrganizationId(): void {
    // Get organization ID from localStorage instead of hardcoding
    const correctOrgId = this.getOrganizationIdFromLocalStorage();
    this.setOrganizationIdInLocalStorage(correctOrgId);

    // Also set the organization tax ID (using the tax ID from your previous API response)
    const orgTaxId = '3edcff5d-3659-4a7b-a54d-e4b6da957d45';
    sessionStorage.setItem('organization_tax_id', orgTaxId);
    //console.log('Organization Tax ID set in localStorage:', orgTaxId);
  }

  /**
   * Debug method to log all tax information
   */
  protected debugTaxInformation(): void {
    //console.log('=== TAX DEBUG INFORMATION ===');
    //console.log('Available tax types:', this.taxTypes);
    //console.log('Organization Tax ID from localStorage:', this.getOrganizationTaxId());
    //console.log('Organization ID from localStorage:', this.getOrganizationIdFromLocalStorage());

    // Log all localStorage keys that might contain tax info
    const taxRelatedKeys = Object.keys(localStorage).filter(key =>
      key.toLowerCase().includes('tax') || key.toLowerCase().includes('org')
    );
    //console.log('Tax/Org related localStorage keys:', taxRelatedKeys);

    taxRelatedKeys.forEach(key => {
      //console.log(`${key}:`, localStorage.getItem(key));
    });
    //console.log('=== END TAX DEBUG ===');
  }

  /**
   * Debug method to log all localStorage content
   */
  protected debugLocalStorage(): void {
    //console.log('=== LOCALSTORAGE DEBUG ===');
    //console.log('All localStorage keys:', Object.keys(localStorage));

    Object.keys(localStorage).forEach(key => {
      const value = localStorage.getItem(key);
      //console.log(`${key}:`, value);

      // Try to parse JSON if it looks like JSON
      if (value && (value.startsWith('{') || value.startsWith('['))) {
        try {
          const parsed = JSON.parse(value);
          //console.log(`${key} (parsed):`, parsed);
        } catch (e) {
          // Not valid JSON, ignore
        }
      }
    });
    //console.log('=== END LOCALSTORAGE DEBUG ===');
  }

  /**
   * Set organization ID directly in localStorage
   */
  protected setOrgIdDirectly(): void {
    const orgId = this.getOrganizationIdFromLocalStorage();
    sessionStorage.setItem('organization_id', orgId);
    sessionStorage.setItem('org_id', orgId);
    sessionStorage.setItem('current_organization_id', orgId);
    //console.log('Organization ID set directly in localStorage:', orgId);
  }

  /**
   * Set the exact tax ID from the example
   */
  protected setExactTaxId(): void {
    const exactTaxId = '3edcff5d-3659-4a7b-a54d-e4b6da957d45';
    sessionStorage.setItem('organization_tax_id', exactTaxId);
    //console.log('Exact tax ID set in localStorage:', exactTaxId);
  }

  /**
   * Try creating service with different payload structures
   */
  protected tryAlternativeServiceCreation(formData: any, selectedUnitType: any, selectedTaxType: any, uploadedImages: string[], orgId: string, orgTaxId: string | null): void {
    //console.log('=== TRYING ALTERNATIVE SERVICE CREATION ===');

    // Try different payload structures using the exact format provided
    const alternativePayloads = [
      {
        name: 'Alternative 1 - Exact payload structure with service_tax_id',
        data: {
          name: formData.nameValue || '',
          key: (formData.nameValue || ''),
          description: formData.descriptionValue || '',
          service_type: 'facility',
          icon_url: null,
          icon_color: null,
          bg_color_light: null,
          bg_color_dark: null,
          exisitng_service_id: null,
          id: null,
          service_tax_id: selectedTaxType?.id || null,
          capacity: parseInt(formData.capacityValue || '0'),
          media_assets: uploadedImages,
          org_id: orgId,
          parking_id: null,
          status: formData.statusValue === 'Active'
        }
      },
      {
        name: 'Alternative 2 - Using organization tax ID from localStorage',
        data: {
          name: formData.nameValue || '',
          key: (formData.nameValue || ''),
          description: formData.descriptionValue || '',
          service_type: 'facility',
          icon_url: null,
          icon_color: null,
          bg_color_light: null,
          bg_color_dark: null,
          exisitng_service_id: null,
          service_tax_id: orgTaxId || selectedTaxType?.id || null,
          capacity: parseInt(formData.capacityValue || '0'),
          media_assets: uploadedImages,
          org_id: orgId,
          parking_id: null,
          status: formData.statusValue === 'Active'
        }
      },
      {
        name: 'Alternative 3 - Using the exact tax ID from your example',
        data: {
          name: formData.nameValue || '',
          key: (formData.nameValue || ''),
          description: formData.descriptionValue || '',
          service_type: 'facility',
          icon_url: null,
          icon_color: null,
          bg_color_light: null,
          bg_color_dark: null,
          exisitng_service_id: null,
          service_tax_id: '3edcff5d-3659-4a7b-a54d-e4b6da957d45',
          capacity: parseInt(formData.capacityValue || '0'),
          media_assets: uploadedImages,
          org_id: orgId,
          parking_id: null,
          status: formData.statusValue === 'Active'
        }
      }
    ];

    // Try each alternative payload
    alternativePayloads.forEach((alternative, index) => {
      //console.log(`Trying ${alternative.name}:`, alternative.data);

      setTimeout(() => {
        this.apiService.createService(alternative.data).subscribe({
          next: (response) => {
            //console.log(`${alternative.name} SUCCESS:`, response);
          },
          error: (error) => {
            //console.log(`${alternative.name} FAILED:`, error);
          }
        });
      }, index * 1000) // Delay each attempt by 1 second
    });
  }

  /**
   * Get organization's default tax ID
   */
  protected getOrganizationTaxId(): string | null {
    // Try to get from localStorage
    const orgTaxId = sessionStorage.getItem('organization_tax_id');
    if (orgTaxId) {
      return orgTaxId;
    }

    // Try to get from user data
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.organization_tax_id) {
          return user.organization_tax_id;
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }

    // Return null if not found
    return null;
  }

  /**
   * Get organization ID from localStorage
   */
  protected getOrganizationIdFromLocalStorage(): string {
    // Get organization ID from localStorage
    const storedOrgId = sessionStorage.getItem('organization_id');
    if (storedOrgId) {
      //console.log('Found organization ID in localStorage:', storedOrgId);
      return storedOrgId;
    }

    // Try to get from user data in localStorage
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        //console.log('User data from localStorage:', user);
        if (user.organization_id) {
          //console.log('Found organization_id in user data:', user.organization_id);
          return user.organization_id;
        }
        if (user.org_id) {
          //console.log('Found org_id in user data:', user.org_id);
          return user.org_id;
        }
      } catch (e) {
        console.error('Error parsing user data from localStorage:', e);
      }
    }

    // Fallback to API service method
    const apiOrgId = this.apiService.getCurrentOrganizationId();
    if (apiOrgId) {
      //console.log('Using organization ID from API service:', apiOrgId);
      return apiOrgId;
    }

    // Final fallback - try to get from user data
    const currentUser = this.apiService.getCurrentUser();
    if (currentUser && currentUser.organization_id) {
      //console.log('Using organization ID from current user:', currentUser.organization_id);
      return currentUser.organization_id;
    }

    console.warn('No organization ID found in any source');
    // If still no organization ID, return a default value
    const fallbackOrgId = 'default-org-id';
    //console.log('Using fallback organization ID:', fallbackOrgId);
    return fallbackOrgId;
  }

  // Prevent form submission on Enter key press
  protected onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const target = event.target as HTMLElement;
      const isMultiline = target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if (!isMultiline) {
        event.preventDefault();
      }
    }
  }

  protected onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const target = event.target as HTMLElement;
      const isMultiline = target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if (!isMultiline) {
        event.preventDefault();
      }
    }
  }

  /**
   * Handle name field blur event - make field readonly after user leaves it
   */
  protected onNameFieldBlur(): void {
    // Only make readonly if not in edit mode and field has a value
    if (!this.isEditMode && this.testForm.get('nameValue')?.value) {
      this.isNameFieldReadonly.set(true);
    }
  }

  protected onlyNumbers(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
    }
  }

  protected onOnlyNumbersInput(event: any, controlName: string = 'capacityValue'): void {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/[^0-9]/g, '');
    input.value = cleanValue;
    if (controlName && this.testForm.get(controlName)) {
      this.testForm.get(controlName)?.setValue(cleanValue, { emitEvent: false });
    }
  }
}
